import React, { useEffect, useState } from 'react'
import {
  getCertifications,
  addCertification,
  removeCertification,
  getLookupValues,
} from '@/api/tapResources'
import { TapApplicationCertification, LabLookupValue } from '@/types/index'
import { Plus, X } from 'lucide-react'

// Feature #218 — Certifications panel.
// Loads the application's certifications and the lookup library for typeahead
// suggestions. Lets the user add a certification (name + issuing authority)
// and remove (soft-delete) an existing one.
interface CertificationsPanelProps {
  applicationId: string
}

const cardStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-white)',
  border: '1px solid var(--color-light-grey)',
  borderRadius: '8px',
  padding: '20px',
  marginBottom: '16px',
}

const inputStyle: React.CSSProperties = {
  padding: '8px 12px',
  border: '1px solid var(--color-light-grey)',
  borderRadius: '6px',
  fontSize: '14px',
  fontFamily: 'var(--font-family)',
  backgroundColor: 'var(--color-white)',
}

const pillStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  padding: '6px 10px',
  border: '1px solid var(--color-light-grey)',
  borderRadius: '999px',
  fontSize: '13px',
  backgroundColor: 'var(--color-white)',
}

const CertificationsPanel: React.FC<CertificationsPanelProps> = ({ applicationId }) => {
  const [certs, setCerts] = useState<TapApplicationCertification[]>([])
  const [suggestions, setSuggestions] = useState<LabLookupValue[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [newName, setNewName] = useState('')
  const [newAuthority, setNewAuthority] = useState('')
  const [busy, setBusy] = useState(false)
  const [showSuggestions, setShowSuggestions] = useState(false)

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      const [certsResult, suggestionsResult] = await Promise.all([
        getCertifications(applicationId),
        getLookupValues('certification'),
      ])
      setCerts(certsResult)
      setSuggestions(suggestionsResult)
    } catch {
      setError('Unable to load certifications')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId])

  const filteredSuggestions = suggestions.filter(
    (s) =>
      s.value.toLowerCase().includes(newName.toLowerCase()) &&
      newName.length > 0,
  )

  const handleAdd = async () => {
    if (!newName.trim()) return
    setBusy(true)
    try {
      await addCertification(applicationId, {
        certificationName: newName.trim(),
        issuingAuthority: newAuthority.trim() || undefined,
      })
      setNewName('')
      setNewAuthority('')
      await load()
    } catch {
      alert('Failed to add certification')
    } finally {
      setBusy(false)
    }
  }

  const handleRemove = async (cert: TapApplicationCertification) => {
    if (!confirm(`Remove "${cert.certificationName}" from this application?`)) return
    setBusy(true)
    try {
      await removeCertification(applicationId, cert.id)
      await load()
    } catch {
      alert('Failed to remove certification')
    } finally {
      setBusy(false)
    }
  }

  const activeCerts = certs.filter((c) => c.isActive)

  return (
    <div style={cardStyle}>
      <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600, color: 'var(--color-black)' }}>
        Certifications
      </h3>

      {loading && (
        <div style={{ fontSize: '14px', color: 'var(--color-dark-grey)' }}>Loading certifications...</div>
      )}

      {!loading && error && (
        <div style={{ fontSize: '14px', color: 'var(--color-magenta)' }}>{error}</div>
      )}

      {!loading && !error && (
        <>
          {activeCerts.length === 0 ? (
            <div style={{ fontSize: '14px', color: 'var(--color-dark-grey)', marginBottom: '12px' }}>
              No certifications captured yet.
            </div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
              {activeCerts.map((cert) => (
                <span key={cert.id} style={pillStyle}>
                  <strong>{cert.certificationName}</strong>
                  {cert.issuingAuthority && (
                    <span style={{ color: 'var(--color-dark-grey)', fontSize: '12px' }}>
                      ({cert.issuingAuthority})
                    </span>
                  )}
                  <button
                    onClick={() => handleRemove(cert)}
                    disabled={busy}
                    aria-label="Remove certification"
                    style={{
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      color: 'var(--color-dark-grey)',
                      padding: '2px',
                      display: 'inline-flex',
                      alignItems: 'center',
                    }}
                  >
                    <X size={14} />
                  </button>
                </span>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <div style={{ position: 'relative', minWidth: '220px' }}>
              <input
                type="text"
                value={newName}
                onChange={(e) => {
                  setNewName(e.target.value)
                  setShowSuggestions(true)
                }}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => {
                  // Delay hiding so click on suggestion registers
                  setTimeout(() => setShowSuggestions(false), 200)
                }}
                placeholder="Certification name..."
                disabled={busy}
                style={{ ...inputStyle, width: '100%' }}
              />
              {showSuggestions && filteredSuggestions.length > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: 'var(--color-white)',
                    border: '1px solid var(--color-light-grey)',
                    borderRadius: '6px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    zIndex: 10,
                    maxHeight: '160px',
                    overflowY: 'auto',
                  }}
                >
                  {filteredSuggestions.map((s) => (
                    <div
                      key={s.id}
                      onMouseDown={() => {
                        setNewName(s.value)
                        setShowSuggestions(false)
                      }}
                      style={{
                        padding: '8px 12px',
                        fontSize: '13px',
                        cursor: 'pointer',
                      }}
                    >
                      {s.value}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <input
              type="text"
              value={newAuthority}
              onChange={(e) => setNewAuthority(e.target.value)}
              placeholder="Issuing authority..."
              disabled={busy}
              style={{ ...inputStyle, minWidth: '180px' }}
            />
            <button
              onClick={handleAdd}
              disabled={busy || !newName.trim()}
              style={{
                padding: '8px 14px',
                backgroundColor: 'var(--color-magenta)',
                color: 'var(--color-white)',
                border: 'none',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: 500,
                cursor: busy || !newName.trim() ? 'not-allowed' : 'pointer',
                opacity: busy || !newName.trim() ? 0.6 : 1,
                fontFamily: 'var(--font-family)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <Plus size={14} /> Add
            </button>
          </div>
        </>
      )}
    </div>
  )
}

export default CertificationsPanel
